import Alpine from 'alpinejs';
import {initUI} from '../core/ui-initializer.js';

/**
 * Dropdown store for Alpine.js
 * Manages dropdown menus with positioning, scroll tracking, and auto-close
 */
export function createDropdownStore() {
    return {
        active: null,
        position: { top: 0, left: 0 },
        autoClose: true,
        _boundHandler: null,
        _scrollHandler: null,
        _resizeHandler: null,
        _hoverHandler: null,
        _currentElement: null,

        open(id, $el) {
            this._currentElement = $el;
            this.updatePosition($el);
            this.active = id;

            this.bindGlobalClose();
            this.bindScrollAndResize();

            Alpine.nextTick(() => {
                const source = document.getElementById(id);
                const target = document.querySelector('[x-ref="dropdown"]');

                target.innerHTML = '';
                target.appendChild(source.content.cloneNode(true));

                htmx.process(target);
                initUI(target);

                // Close the dropdown when clicking a button or link inside it
                const buttons = target.querySelectorAll('button, a');
                buttons.forEach(button => {
                    button.addEventListener('click', () => this.close());
                });
            });
        },

        updatePosition($el) {
            if (!$el) return;
            const rect = $el.getBoundingClientRect();
            this.position = {
                top: rect.bottom + window.scrollY + 4,
                left: rect.right + window.scrollX - 160
            };
        },

        close() {
            this.active = null;
            this._currentElement = null;
            this.unbindGlobalClose();
            this.unbindScrollAndResize();
        },

        bindGlobalClose() {
            if (!this.autoClose || this._boundHandler) return;

            this._boundHandler = (event) => {
                if (!this.active) return;

                const target = document.querySelector('[x-ref="dropdown"]');
                const isClickInsideDropdown = target && target.contains(event.target);
                const isClickOnButton = this._currentElement && this._currentElement.contains(event.target);

                // Only close if the click is outside both the dropdown and the trigger button
                if (!isClickInsideDropdown && !isClickOnButton) {
                    this.close();
                }
            };

            document.addEventListener('click', this._boundHandler, true);
        },

        unbindGlobalClose() {
            if (!this._boundHandler) return;

            document.removeEventListener('click', this._boundHandler, true);
            this._boundHandler = null;
        },

        bindScrollAndResize() {
            if (this._scrollHandler) return;

            this._scrollHandler = () => {
                if (this._currentElement && this.active) {
                    this.updatePosition(this._currentElement);
                }
            };

            this._resizeHandler = () => {
                if (this._currentElement && this.active) {
                    this.updatePosition(this._currentElement);
                }
            };

            this._hoverHandler = () => {
                if (this._currentElement && this.active) {
                    setTimeout(() => this.updatePosition(this._currentElement), 0);
                }
            };

            window.addEventListener('scroll', this._scrollHandler, true);
            window.addEventListener('resize', this._resizeHandler);

            // Bind hover handlers to the parent card if it exists
            if (this._currentElement) {
                const card = this._currentElement.closest('.group, [class*="group"]');
                if (card) {
                    card.addEventListener('mouseenter', this._hoverHandler);
                    card.addEventListener('mouseleave', this._hoverHandler);
                    this._currentCard = card;
                }
            }
        },

        unbindScrollAndResize() {
            if (this._scrollHandler) {
                window.removeEventListener('scroll', this._scrollHandler, true);
                this._scrollHandler = null;
            }

            if (this._resizeHandler) {
                window.removeEventListener('resize', this._resizeHandler);
                this._resizeHandler = null;
            }

            if (this._hoverHandler && this._currentCard) {
                this._currentCard.removeEventListener('mouseenter', this._hoverHandler);
                this._currentCard.removeEventListener('mouseleave', this._hoverHandler);
                this._hoverHandler = null;
                this._currentCard = null;
            }
        }
    };
}
